import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "../../router";
import { ClientTypeIcon } from "../../components/ClientTypeIcon";
import {
  advisoryApi,
  isUnavailable,
  type Applicability,
  type Goal,
  type Prefill,
  type SessionState,
  type StatusStep,
} from "./advisoryApi";
import { AdviceReview } from "./AdviceReview";
import { AfterSubmit } from "./AfterSubmit";
import { ClientContext, type PrefillAction } from "./ClientContext";
import { isEditable, isInFlight, statusLabel, statusPill } from "./format";
import { buildNav, firstOpen, REVIEW_ID, WEALTH_PLAN_ID, type NavItem } from "./nav";
import { StepPager } from "./StepPager";
import { PREFILL_KEY, StepPage } from "./StepPage";
import { StepNav } from "./StepNav";
import { Timeline } from "./Timeline";
import { WealthPlanStep } from "./steps/WealthPlanStep";
import { PoweredBy, Unavailable } from "./ui";
import "./advisory.css";

const POLL_MS = 10000;
// A completed advice whose orders haven't reached the inbox yet: keep looking,
// gently (the server throttles the inbox pull itself).
const ORDERS_POLL_MS = 30000;

const PREFILL_ACTIONS: Record<string, Omit<PrefillAction, "componentType">> = {
  financial_situation: {
    label: "Use Andersen holdings",
    description: "Fill the assets with the client's Andersen accounts — securities and cash, converted to the advice currency.",
  },
  risk_assessment: {
    label: "Use Andersen risk profile",
    description: "Propose the risk level from the client's Andersen risk profile. The horizon and questions stay yours.",
  },
  portfolio_input: {
    label: "Use uninvested cash",
    description: "Propose a one-time investment of the cash the client holds uninvested at Andersen.",
  },
};

function readStepParam(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("step");
}

/**
 * The advisor workbench (§4.3): steps on the left (QP's pages, in the
 * advisor's fixed order — nav.ts),
 * the current step in the centre, Andersen's client context on the right.
 * Driven by /status: every save returns a fresh one and the navigator follows.
 */
export function Workbench({ sessionId }: { sessionId: number }) {
  const [state, setState] = useState<SessionState | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loadErr, setLoadErr] = useState<{ msg: string; unavailable: boolean; notFound: boolean } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(readStepParam);
  const [attempted, setAttempted] = useState(false);
  const [prefill, setPrefill] = useState<{ componentType: string; itemId: string; data: Prefill } | null>(null);
  const [prefillBusy, setPrefillBusy] = useState<string | null>(null);
  const [prefillErr, setPrefillErr] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [applicability, setApplicability] = useState<Applicability | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncErr, setSyncErr] = useState<string | null>(null);
  const inflight = useRef(false);

  const loadGoals = useCallback(() => {
    advisoryApi
      .goals(sessionId)
      .then((d) => setGoals(d.data))
      .catch(() => {});
  }, [sessionId]);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const s = await advisoryApi.session(sessionId);
      setState(s);
      setLoadErr(null);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setLoadErr({ msg: (e as Error).message, unavailable: isUnavailable(e), notFound: status === 404 });
    } finally {
      inflight.current = false;
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
    loadGoals();
  }, [load, loadGoals]);

  // The Refresh after submit: re-read QP and drive the gate/inbox pipeline now.
  const sync = useCallback(async () => {
    setSyncing(true);
    setSyncErr(null);
    try {
      const s = await advisoryApi.sync(sessionId);
      setState(s);
      setVersion((v) => v + 1);
    } catch (e) {
      setSyncErr(isUnavailable(e) ? "Quantfolio didn't answer just now — try again in a moment." : (e as Error).message);
    } finally {
      setSyncing(false);
    }
  }, [sessionId]);

  const sessionStatus = state?.session.status ?? null;
  const editable = isEditable(sessionStatus);
  const inbox = state?.pipeline?.inbox;
  const awaitingOrders = sessionStatus === "completed" && !!inbox && inbox.orders + inbox.savingsPlans === 0;

  // After submit, follow the process (compliance → external → completed), and
  // then the orders into the inbox. Each poll also drives the pipeline server-side.
  useEffect(() => {
    const every = isInFlight(sessionStatus) ? POLL_MS : awaitingOrders ? ORDERS_POLL_MS : null;
    if (every === null) return;
    const tick = () => document.visibilityState === "visible" && void load();
    const t = window.setInterval(tick, every);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [sessionStatus, awaitingOrders, load]);

  // Whether Knowledge & Experience is really open to answer (QP reports it
  // `complete` while nothing needs assessing). Re-read when the steps change.
  const stepsKey = (state?.status?.steps ?? []).map((x) => x.status).join(",");
  useEffect(() => {
    if (!editable || !state?.session.flow_instance_id) return;
    let stale = false;
    advisoryApi
      .applicability(sessionId)
      .then((a) => !stale && setApplicability(a))
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [sessionId, editable, stepsKey, version, state?.session.flow_instance_id]);

  const nav = useMemo(() => buildNav(state?.status ?? null, goals, applicability), [state?.status, goals, applicability]);

  // Land on the first open page (or the review once submitted), unless the URL says otherwise.
  useEffect(() => {
    if (!state?.status) return;
    if (selectedId && nav.flat.some((i) => i.id === selectedId)) return;
    const target = editable ? firstOpen(nav.flat) : nav.flat.find((i) => i.id === REVIEW_ID);
    if (target) setSelectedId(target.id);
  }, [state?.status, nav, selectedId, editable]);

  const selected: NavItem | undefined = nav.flat.find((i) => i.id === selectedId);

  const select = useCallback((item: NavItem) => {
    setSelectedId(item.id);
    setPrefill(null);
    setPrefillErr(null);
    const url = new URL(window.location.href);
    url.searchParams.set("step", item.id);
    window.history.replaceState(null, "", url.toString());
    document.querySelector(".adv-centre")?.scrollTo({ top: 0 });
  }, []);

  const onSaved = useCallback(
    (s: SessionState, opts?: { goalsChanged?: boolean }) => {
      setState((prev) => ({ ...s, refreshError: null, process: s.process ?? prev?.process ?? null }));
      setVersion((v) => v + 1);
      if (opts?.goalsChanged) loadGoals();
    },
    [loadGoals],
  );

  const goTo = useCallback(
    (componentType: string) => {
      const target = nav.flat.find((i) => i.steps.some((s) => s.componentType === componentType));
      if (target) select(target);
    },
    [nav, select],
  );

  const goToStep = useCallback(
    (step: StatusStep) => {
      const target = nav.flat.find((i) => i.steps.includes(step));
      if (target) select(target);
    },
    [nav, select],
  );

  async function requestPrefill(componentType: string) {
    if (!selected) return;
    const key = PREFILL_KEY[componentType];
    if (!key) return;
    setPrefillBusy(componentType);
    setPrefillErr(null);
    try {
      const data = await advisoryApi.prefill(sessionId, key, {
        goalId: selected.goalId,
        portfolioId: selected.portfolioId,
      });
      setPrefill({ componentType, itemId: selected.id, data });
    } catch (e) {
      setPrefillErr((e as Error).message);
    } finally {
      setPrefillBusy(null);
    }
  }

  // ---- Render --------------------------------------------------------------------------

  if (!state) {
    return (
      <div className="page adv-page">
        <Link to="/app/advisory" className="back-link">
          ← Advice desk
        </Link>
        {loadErr ? (
          loadErr.unavailable ? (
            <Unavailable message={loadErr.msg} onRetry={() => void load()} />
          ) : (
            <div className="banner-error">{loadErr.notFound ? "This advice session does not exist." : loadErr.msg}</div>
          )
        ) : (
          <p className="muted">Opening the advice…</p>
        )}
      </div>
    );
  }

  const s = state.session;
  const currency = "NOK";
  const actions: PrefillAction[] =
    selected?.kind === "page"
      ? [...new Set(selected.steps.map((x) => x.componentType ?? ""))]
          .filter((ct) => PREFILL_ACTIONS[ct])
          .map((ct) => ({ componentType: ct, ...PREFILL_ACTIONS[ct]! }))
      : [];

  const base = {
    sessionId,
    readOnly: !editable,
    goals,
    currency,
    onSaved,
    clearPrefill: () => setPrefill(null),
    goTo,
  };

  return (
    <div className="adv-workbench">
      <header className="page-head adv-wb-head">
        <div>
          <Link to="/app/advisory" className="back-link">
            ← Advice desk
          </Link>
          <h1>
            {s.investor_type && <ClientTypeIcon type={s.investor_type} className="title-type-icon" />} {s.investor_name ?? "Advice"}{" "}
            <span className={statusPill(s.status)}>{statusLabel(s.status)}</span>
          </h1>
          <p className="muted">
            {s.flow_name ?? "Advice"} · advisor {s.advisor_username ?? "—"}
          </p>
        </div>
      </header>

      {state.refreshError && (
        <div className="adv-calm-banner">
          {/404/.test(state.refreshError)
            ? "This advice no longer exists in the advice engine — it may have been deleted there. What Andersen recorded is shown below."
            : "The advice engine didn't answer just now, so the steps can't be shown. Nothing has been lost."}{" "}
          <button type="button" className="link-action" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {!state.status ? (
        s.status === "failed" ? (
          <div className="banner-error">The advice could not be opened{s.error ? `: ${s.error}` : "."}</div>
        ) : state.refreshError && !/404/.test(state.refreshError) ? (
          <Unavailable onRetry={() => void load()} />
        ) : (
          <section className="adv-step-card">
            <span className="adv-label">History</span>
            <Timeline sessionId={sessionId} refreshKey={String(version)} />
          </section>
        )
      ) : (
        <div className="adv-columns">
          <StepNav groups={nav.groups} status={state.status} selectedId={selectedId} attempted={attempted} onSelect={select} submitted={!editable} />

          <main className="adv-centre">
            {selected && (
              <div className="adv-centre-head">
                <span className="eyebrow">{selected.kind === "page" ? (selected.subtitle ?? "Advice") : "Advice"}</span>
                <h2 className="adv-step-title">{selected.title}</h2>
              </div>
            )}
            {!selected ? (
              <p className="muted">Choose a step on the left.</p>
            ) : selected.kind === "review" ? (
              <>
                {!editable && (
                  <AfterSubmit state={state} onRefresh={() => void load()} onSync={sync} syncing={syncing} syncError={syncErr} />
                )}
                <AdviceReview
                  key={`${s.status}`}
                  state={state}
                  onSubmitted={(ns) => {
                    onSaved(ns);
                    setAttempted(false);
                  }}
                  onAttempt={() => setAttempted(true)}
                  onGoTo={goToStep}
                />
                <section className="adv-subsection">
                  <span className="adv-label">History</span>
                  <Timeline sessionId={sessionId} refreshKey={`${version}:${s.status}`} />
                </section>
              </>
            ) : selected.id === WEALTH_PLAN_ID ? (
              <WealthPlanStep sessionId={sessionId} readOnly={!editable} onSaved={onSaved} currency={currency} />
            ) : (
              <StepPage
                key={selected.id}
                item={selected}
                base={base}
                attempted={attempted}
                prefillFor={(ct) => (prefill && prefill.componentType === ct && prefill.itemId === selected.id ? prefill.data : null)}
              />
            )}
            {selected && <StepPager flat={nav.flat} selectedId={selected.id} onSelect={select} editable={editable} />}
            <PoweredBy />
          </main>

          <ClientContext
            investorId={s.investor_id}
            actions={actions}
            busy={prefillBusy}
            error={prefillErr}
            onPrefill={requestPrefill}
            editable={editable}
          />
        </div>
      )}
    </div>
  );
}
